// Copyright (c) 2022 by Move Closer

import { ComponentInternalInstance, onMounted, PropType, ref, watch } from '@vue/composition-api'
import debounce from 'lodash/debounce'

import { ComponentObjectPropsOptions } from '../../vue-api'
import { FormControlType } from '../../abstract/general'

import {
  DEBOUNCE_DELAY,
  DEFAULT_BUTTON_TEXT,
  DEFAULT_MIN_VALUE_LENGTH,
  DEFAULT_SEARCH_INPUT_NAME,
  DEFAULT_SEARCH_INPUT_PLACEHOLDER,
  DEFAULT_SEARCH_INPUT_VALUE,
  KEY_CODES
} from './SearchInput.config'
import { SearchInputProps, UseSearchInputProvides } from './SearchInput.contracts'

/**
 * @author Maciej Perzankowski <maciej.perzankowski@movecloser.pl>
 */
export const customSearchInputProps: ComponentObjectPropsOptions<SearchInputProps> = {
  /**
   * Determines button text.
   */
  buttonContent: {
    type: String,
    required: false,
    default: DEFAULT_BUTTON_TEXT
  },

  /**
   * Value for the `[collapsable]` attribute.
   */
  collapsable: {
    type: Boolean,
    required: true
  },

  /**
   * Determine if input should be focused on mount.
   */
  focusOnMount: {
    type: Boolean,
    required: false,
    default: false
  },

  /**
   * Determines whether input has button.
   */
  hasButton: {
    type: Boolean,
    required: false,
    default: false
  },

  /**
   * Determines whether the search input should have close icon
   */
  hasCloseIcon: {
    type: Boolean,
    required: false,
    default: true
  },

  /**
   * Value for the `[name]` attribute.
   */
  name: {
    type: String as PropType<FormControlType>,
    required: false,
    default: DEFAULT_SEARCH_INPUT_NAME,
    validator (name: string): boolean {
      return (typeof name !== 'undefined') && (name.length > 0)
    }
  },

  /**
   * Value for the `[openOnMount]` attribute.
   */
  openOnMount: {
    type: Boolean,
    required: false,
    default: false
  },

  /**
   * Value for the `[placeholder]` attribute.
   */
  placeholder: {
    type: String as PropType<FormControlType>,
    required: false,
    default: DEFAULT_SEARCH_INPUT_PLACEHOLDER,
    validator (placeholder: string): boolean {
      return (typeof placeholder !== 'undefined') && (placeholder.length > 0)
    }
  },

  /**
   * Determines whether input should debounce
   */
  shouldDebounce: {
    type: Boolean,
    required: false,
    default: true
  },

  /**
   * Value for the `[searchFromChars]` attribute.
   */
  searchFromChars: {
    type: Number,
    required: false,
    default: DEFAULT_MIN_VALUE_LENGTH,
    validator (minLengthValue: number): boolean {
      return (typeof minLengthValue !== 'undefined') && (minLengthValue > 0)
    }
  },

  /**
   * Value for the `[value]` attribute.
   */
  value: {
    type: String as PropType<FormControlType>,
    required: false,
    default: DEFAULT_SEARCH_INPUT_VALUE
  }
}

/**
 * @author Maciej Perzankowski <maciej.perzankowski@movecloser.pl>
 */
export const useSearchInput = (
  props: SearchInputProps,
  internalInstance: ComponentInternalInstance | null
): UseSearchInputProvides => {
  if (!internalInstance) {
    throw new Error('useSearchInput(): Instance for component is not found!')
  }

  /**
   * Determines whether is input open.
   */
  const isOpen = ref<boolean>(props.collapsable ? (props.openOnMount || false) : true)

  /**
   * @see UseSearchInputProvides._value
   */
  const model = ref<string>(props.value || DEFAULT_SEARCH_INPUT_VALUE)
  const oldValue = ref<string>(model.value)

  const clearInput = (): void => {
    model.value = ''
  }

  const collapseInput = (): void => {
    isOpen.value = false
  }

  const debounceDelay = (): number => {
    return props.debounceDelay ?? (!props.shouldDebounce ? 0 : DEBOUNCE_DELAY)
  }

  const open = (focus: boolean = true): void => {
    const searchInputRef = (internalInstance.refs.searchInputRef) as HTMLElement

    if (!searchInputRef) {
      return
    }

    isOpen.value = true
    if (focus) {
      searchInputRef.focus()
    }
  }

  const removeListeners = (): void => {
    const searchInputRef = (internalInstance.refs.searchInputRef) as HTMLElement

    if (!searchInputRef) {
      return
    }

    searchInputRef.removeEventListener('keydown', onKeyDown)
  }

  const registerListeners = (): void => {
    const searchInputRef = (internalInstance.refs.searchInputRef) as HTMLElement

    if (!searchInputRef) {
      return
    }

    searchInputRef.addEventListener('keydown', onKeyDown)
  }

  /**
   * @see UseSearchInputProvides.onBlur
   */
  const onBlur = (): void => {
    if (!props.collapsable || !!model.value) {
      return
    }

    collapseInput()
    removeListeners()
  }

  /**
   * @see UseSearchInputProvides.onCancel
   */
  const onCancel = (): void => {
    const searchInputRef = (internalInstance.refs.searchInputRef) as HTMLElement

    if (!searchInputRef) {
      return
    }

    clearInput()
    onBlur()

    internalInstance.emit('cancel')
  }

  const onFocus = (e: FocusEvent): void => {
    internalInstance.emit('focus', e)
    registerListeners()
  }

  const onChange = debounce(
    (value: string): void => {
      internalInstance.emit('input', value)
      oldValue.value = value
    },
    debounceDelay()
  )

  const handleButtonClick = () => {
    submit()
  }

  const onKeyDown = (event: KeyboardEvent): void => {
    if (props.collapsable && (event.keyCode === KEY_CODES.ESCAPE)) {
      onCancel()
    }

    if (event.keyCode === KEY_CODES.ENTER) {
      submit()
    }
  }

  const onInputClick = (): void => {
    internalInstance.emit('onInputClick')
  }

  const submit = (): void => {
    internalInstance.emit('searchInputSubmit', model.value)
    oldValue.value = model.value
  }

  watch(model, onChange)

  onMounted(() => {
    if (model.value) {
      open(props.collapsable ? true : props.focusOnMount)
    }
  })

  return { isOpen, model, onBlur, onCancel, onFocus, open, onInputClick, handleButtonClick }
}
